import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Heading,
  VStack,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Flex,
  Icon,
  Skeleton,
  useColorModeValue,
  HStack,
  useToast,
  Button,
  Container,
  Stack,
  Spacer,
  Link,
} from "@chakra-ui/react";
import { FaLeaf, FaWifi, FaBell } from "react-icons/fa";
import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";
import MoistureWidget from "../../components/FieldComponents.tsx/MoistureWidget";
import NotesWidget from "../../components/FieldComponents.tsx/NotesWidget";
import DeviceMapView from "../DeviceMapView";
import { GiValve } from "react-icons/gi";
import TableForecastWidget from "../../components/FieldComponents.tsx/TableForecastWidget";
import { Link as RouterLink } from "react-router-dom";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { AlarmOccurrence, IFieldAlarmSummary } from "../../types/alarmInterface";
import { IField, IStatus } from "../../types/fieldInterface";
import { getFieldStatusLabel } from "../../utils/fieldStatusUtils";
import { IDevice } from "../../types/deviceInterface";
import {
  CurrentWeather,
  HourlyData,
  IWeatherCurrentDaily,
  IWeatherForecast,
} from "../../types/weatherInterface";
import { weatherCodeToIcon, weatherDescription } from "../../utils/weatherIconUtils";
import { getCropTypeIcon } from "../../utils/cropTypeMap";
import { translateCropTypeName } from "../../utils/translateCropTypeName";
import { useTileColors } from "../../utils/colorUtils/tileColorUtil";
import { useMoistureColor } from "../../utils/colorUtils/soilMoistitureColorUtil";
import { useHoverColors } from "../../utils/colorUtils/hoverColorUtil";

const FieldView = () => {
  const api = useAxios();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [field, setField] = useState<IField>();
  const [sensors, setSensors] = useState<any[]>([]);
  const [forecastDaily, setForecastDaily] = useState<IWeatherForecast["daily"] | null>(null);
  const [current, setCurrent] = useState<CurrentWeather | null>(null);
  const [valves, setValves] = useState<IDevice[]>([]);
  const [cropType, setCropType] = useState<string | null>(null);
  const [alarmsInfo, setAlarmsInfo] = useState<IFieldAlarmSummary>();
  const [status, setStatus] = useState<IStatus | null>(null);
  const navigate = useNavigate();
  const [loadingMainFlag, setLoadingMain] = useState(true);
  const [loadingDevicesFlag, setLoadingDevices] = useState(true);
  const [loadingWeatherFlag, setLoadingWeather] = useState(true);

  const { noAlarmBgColor, redBgColor, yellowBgColor } = useTileColors();
  const { pwpColor, damageColor, deficitColor, goodColor, highColor, runoffColor, undefinedColor } =
    useMoistureColor();
  const { redHoverColor, yellowHoverColor, noAlarmHoverColor } = useHoverColors();

  const toAlarmSummary = (alarms: AlarmOccurrence[]): IFieldAlarmSummary => {
    const fieldAlarms = alarms.filter((alarm) => alarm.source_kind === "FIELD_TRIGGER").length;
    const deviceAlarms = alarms.filter(
      (alarm) => alarm.source_kind === "SENSOR_DATA_TRIGGER",
    ).length;
    const persistentAlarms = alarms.filter(
      (alarm) => alarm.source_kind === "BATTERY_WATCHER" || alarm.source_kind === "OFFLINE_WATCHER",
    ).length;
    const valveAlarms = alarms.filter((alarm) => alarm.source_kind === "VALVE_WATCHER").length;

    return {
      active_count: alarms.length,
      general_count: alarms.length,
      field_num_active_alarms: {
        device: deviceAlarms,
        field: fieldAlarms,
        persistent: persistentAlarms,
        valve_control: valveAlarms,
      },
      device_alarms_count: deviceAlarms,
      persistent_alarms_count: persistentAlarms,
      valve_control_alarms_count: valveAlarms,
      field_alarms_count: fieldAlarms,
      yellow_alarms_count: alarms.filter((alarm) => alarm.severity === "WARNING").length,
      red_alarms_count: alarms.filter((alarm) => alarm.severity === "ERROR").length,
    };
  };

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setLoadingMain(true);

    (async () => {
      try {
        const fRes = await api.get(`agtech/fields/${id}/`);

        if (cancelled) return;

        setField(fRes.data);

        setCropType(fRes.data.crop_type_details?.name ?? null);
      } catch (err) {
        if (cancelled) return;
        toast({
          title: "Error fetching field data",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        if (!cancelled) setLoadingMain(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setLoadingDevices(true);

    (async () => {
      try {
        const valv = await api.get<IDevice[]>(`frontend/devices/by-field/${id}/`);
        const stat = await api.get<IStatus>(`frontend/devices/status/${id}`);

        if (cancelled) return;

        setSensors(valv.data);
        setStatus(stat.data);

        const onlyValves = valv.data.filter(
          (d: IDevice) => d.device_type === 5 || d.device_type === 6,
        );
        setValves(onlyValves);
        console.log("valves", onlyValves);
        console.log("status for field", stat.data);
        console.log("devices", valv.data);
      } catch (err) {
        if (cancelled) return;
        toast({
          title: "Error fetching devices/status",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        if (!cancelled) setLoadingDevices(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setLoadingWeather(true);

    (async () => {
      try {
        const alaInfo = await api.get<AlarmOccurrence[]>("frontend/alarms/", {
          params: { field: id, state: "active" },
        });
        if (!cancelled) {
          setAlarmsInfo(toAlarmSummary(alaInfo.data));
        }
      } catch (err) {
        if (cancelled) return;
        toast({
          title: "Error fetching alarms",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }

      try {
        const [currentWeatherData, weatherForecastData] = await Promise.all([
          api.get<IWeatherCurrentDaily>(`agtech/weather-forecast/current/${id}`),
          api.get<IWeatherForecast>(`agtech/weather-forecast/daily/${id}/`),
        ]);
        if (cancelled) return;

        setForecastDaily(weatherForecastData.data.daily);
        setCurrent({
          temperature_2m_max: currentWeatherData.data.daily.temperature_2m_max[0],
          temperature_2m_min: currentWeatherData.data.daily.temperature_2m_min[0],
          precipitation_sum: currentWeatherData.data.current.precipitation,
          weather_code: currentWeatherData.data.current.weather_code,
          clous_cover: currentWeatherData.data.current.cloud_cover,
          wind_speed_10m_max: currentWeatherData.data.current.wind_speed_10m,
          actual_temp: currentWeatherData.data.current.temperature_2m,
        });
      } catch (err) {
        if (cancelled) return;
        setCurrent(null);
        setForecastDaily(null);
        toast({
          title: "Error fetching weather",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        if (!cancelled) setLoadingWeather(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const iconColor = useColorModeValue("#16361F", "green.200");

  const soilColorMap: Record<string, string> = {
    PWP: pwpColor,
    Damage: damageColor,
    Deficit: deficitColor,
    Good: goodColor,
    High: highColor,
    Runoff: runoffColor,
    Undefined: undefinedColor,
  };

  function paintSoilStat(color: string): string {
    return soilColorMap[color] ?? color;
  }

  const valveAlarms_active = alarmsInfo?.field_num_active_alarms.valve_control ?? 0;
  const fieldAlarms_active = alarmsInfo?.field_num_active_alarms.field ?? 0;
  const persistentAlarms_active = alarmsInfo?.field_num_active_alarms.persistent ?? 0;
  const deviceAlarms_active = alarmsInfo?.field_num_active_alarms.device ?? 0;

  function defineAlarmColor(): string {
    if (valveAlarms_active + fieldAlarms_active > 0) {
      return redBgColor;
    } else if (persistentAlarms_active + deviceAlarms_active > 0) {
      return yellowBgColor;
    }
    return noAlarmBgColor;
  }

  function defineAlarmHover(): string {
    if (valveAlarms_active + fieldAlarms_active > 0) {
      return redHoverColor;
    } else if (persistentAlarms_active + deviceAlarms_active > 0) {
      return yellowHoverColor;
    }
    return noAlarmHoverColor;
  }

  return (
    <Navbar>
      <Container maxW="7xl" py={6} px={6}>
        <VStack spacing={6} align="stretch">
          <Heading fontSize="3xl">
            {field?.field_name}
            {cropType && (
              <>
                {" "}
                {getCropTypeIcon(field?.crop_type, cropType) ? (
                  <Icon as={getCropTypeIcon(field?.crop_type, cropType)} boxSize={6} ml={1} />
                ) : (
                  translateCropTypeName(cropType)
                )}
              </>
            )}
          </Heading>
          <Button
            colorScheme="black"
            variant="outline"
            w="120px"
            size={"md"}
            borderRadius={"unset"}
            p={2}
            flexShrink={0}
            fontStyle="semibold"
            _hover={{ bg: "blackAlpha.100" }}
            onClick={() => navigate(`/fields/edit/${id}`)}
            h={"32px"}
          >
            <Trans>Edit</Trans>
          </Button>
          <Spacer />

          <Heading fontSize="2xl">
            <Trans>At a glance</Trans>
          </Heading>

          <Skeleton isLoaded={!loadingMainFlag && !loadingDevicesFlag}>
            <SimpleGrid
              spacing={4}
              justifyItems="center"
              alignItems="center"
              columns={{ base: 2, lg: 4 }}
            >
              <Stat
                bg={paintSoilStat(status?.field_status || "Undefined")}
                p={4}
                borderRadius="md"
                h="150px"
                w="170px"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
              >
                <Icon as={FaLeaf} boxSize={8} mb={2} color={iconColor} />
                <StatNumber fontSize="xl">
                  {status ? getFieldStatusLabel(status) : undefined}
                </StatNumber>
                <StatLabel>
                  <Trans>Soil Moisture</Trans>
                </StatLabel>
              </Stat>

              <Stat
                bg={noAlarmBgColor}
                p={4}
                borderRadius="md"
                h="150px"
                w="170px"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                _hover={{ bg: noAlarmHoverColor, shadow: "md", cursor: "pointer" }}
                onClick={() => navigate(`/devices?field=${id}`)}
              >
                <Icon as={FaWifi} boxSize={8} mb={2} color={iconColor} />
                <StatNumber fontSize="xl">{sensors.length}</StatNumber>
                <StatLabel>
                  <Trans>Sensors</Trans>
                </StatLabel>
              </Stat>
              <Stat
                bg={noAlarmBgColor}
                p={4}
                borderRadius="md"
                h="150px"
                w="170px"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                _hover={{ bg: noAlarmHoverColor, shadow: "md", cursor: "pointer" }}
                onClick={() => navigate(`/devices?field=${id}&device_type=6`)}
              >
                <Icon as={GiValve} boxSize={8} mb={2} color={iconColor} />
                <StatNumber fontSize="xl">{valves.length}</StatNumber>
                <StatLabel>
                  <Trans>Irrigation Valve</Trans>
                </StatLabel>
              </Stat>

              <Stat
                bg={defineAlarmColor()}
                p={4}
                borderRadius="md"
                h="150px"
                w="170px"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                onClick={() => navigate(`/fields/alarms/${id}`)}
                _hover={{
                  bg: defineAlarmHover(),
                  shadow: "md",
                  cursor: "pointer",
                }}
              >
                <Icon as={FaBell} boxSize={8} mb={2} color={iconColor} />
                <StatNumber fontSize="xl">{alarmsInfo?.active_count}</StatNumber>
                <StatLabel>
                  <Trans>Active Alerts</Trans>
                </StatLabel>
              </Stat>
            </SimpleGrid>
          </Skeleton>

          <Flex direction={{ base: "column", md: "row" }} gap={4}>
            <Box flex="2" p={4} mt={6}>
              <Heading fontSize="2xl" mb={4} ml={0} pl={0}>
                <Trans>Today's Weather</Trans>
              </Heading>
              {current && (
                <VStack align="start" spacing={1}>
                  <Stack>
                    <Skeleton isLoaded={!loadingWeatherFlag} mb={2}>
                      <HStack spacing={3} align="center">
                        <Icon as={weatherCodeToIcon[current.weather_code]} boxSize={9} />

                        <VStack spacing={0} align="start">
                          <Text fontSize="2xl" fontWeight="bold">
                            {current.actual_temp !== undefined
                              ? current.actual_temp.toFixed(1)
                              : "--"}
                            °
                          </Text>

                          <Text fontSize="md" fontWeight="semibold">
                            {weatherDescription(current.weather_code)}
                          </Text>
                        </VStack>
                      </HStack>
                    </Skeleton>

                    <Text fontSize="sm">
                      <Trans>Min/Max:</Trans> {current.temperature_2m_min.toFixed(1)}° –{" "}
                      {current.temperature_2m_max.toFixed(1)}°
                    </Text>
                    <Text fontSize="sm">
                      <Trans>Precipitation:</Trans> {current.precipitation_sum.toFixed(1)} mm
                    </Text>
                    <Text fontSize="sm">
                      <Trans>Wind:</Trans> {current.wind_speed_10m_max.toFixed(0)} m/s
                    </Text>
                    <Text fontSize="sm">
                      <Trans>Cloud Cover:</Trans> {current.clous_cover}%
                    </Text>
                  </Stack>
                </VStack>
              )}
            </Box>
            <Box flex="2" p={4} mt={6}>
              <Skeleton isLoaded={!loadingWeatherFlag} height={"380px"}>
                <Box maxW={{ base: "100%", md: "600px" }} mx="auto">
                  <Flex justify={"space-between"}>
                    <Heading fontSize="2xl" mb={3} textAlign="left">
                      <Trans>7-day Forecast</Trans>
                    </Heading>

                    <Link as={RouterLink} to={`/field/${id}/additional-info`} fontSize="md">
                      <Trans>Show More</Trans>
                    </Link>
                  </Flex>

                  <VStack
                    spacing={0}
                    align="stretch"
                    maxW={{ base: "100%", md: "600px" }}
                    mx="auto"
                  >
                    {forecastDaily?.time.slice(0, 7).map((dateStr, idx) => (
                      <TableForecastWidget
                        key={dateStr}
                        dateStr={dateStr}
                        weatherCode={forecastDaily.weather_code[idx]}
                        tMax={forecastDaily.temperature_2m_max[idx].toFixed(0)}
                        wMax={forecastDaily.wind_speed_10m_max[idx].toFixed(1)}
                        isLast={idx === forecastDaily.time.slice(0, 7).length}
                      />
                    ))}
                  </VStack>
                </Box>
              </Skeleton>
            </Box>
          </Flex>

          <Heading fontSize="2xl" marginTop={6}>
            <Trans>Map Overview</Trans>
          </Heading>
          <Box overflow="hidden">
            <Skeleton isLoaded={!loadingMainFlag}>
              {field && (
                <DeviceMapView
                  embedded
                  lockToField
                  hideFieldSelector
                  hideFieldLink
                  hideFieldSummary
                  initialFieldId={id ? Number(id) : undefined}
                />
              )}
            </Skeleton>
          </Box>
          <Box p={{ base: 0, md: 4 }}>
            <Skeleton isLoaded={!loadingMainFlag}>
              {field && <MoistureWidget field={field} />}
            </Skeleton>
          </Box>
          <Box p={{ base: 0, md: 4 }}>
            <Skeleton isLoaded={!loadingMainFlag}>
              {field && <NotesWidget field={field} />}
            </Skeleton>
          </Box>
          <Box p={{ base: 0, md: 4 }}>
            <Heading fontSize="2xl" mb={4}>
              <Trans>More tools</Trans>
            </Heading>
            <Link as={RouterLink} to={`/fields/satellite/${id}`} fontSize="md">
              <Trans>View Satellite Imagery</Trans>
            </Link>
          </Box>
        </VStack>
      </Container>
    </Navbar>
  );
};

export default FieldView;
